import React, { forwardRef, useEffect } from "react";
import { CardBody, CardFooter, Col, Row } from "reactstrap";
import { formatDate } from "utils/date";
import "utils/jsdocTypes";
import "./receipt.style.css";
import ReceiptLineItem from "./receiptLineItem.component";

/**
 * @typedef {Object} Props
 * @property {AdminTransactionDetails} transaction
 * @property {AdminCustomerDetails} customer
 */

/**
 * @function ReceiptCard
 * @param {Props} Props
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<Props> & React.RefAttributes<HTMLDivElement>>}
 */
const ReceiptCard = forwardRef(({ transaction, customer, onCardLoad }, ref) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onCardLoad) {
        onCardLoad();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [onCardLoad]);

  return (
    <div className="Receipt receiptWrapper gap-0" ref={ref}>
      <CardBody>
        <Row>
          <Col sm="12" className="receiptTextCenter receiptLogo">
            <img src="/logo.png" crossOrigin="anonymous" />
          </Col>
        </Row>

        <Row>
          <Col sm="12" className="receiptTextCenter receiptTitle">
            <span>Your purchase of cloud space has been processed</span>
          </Col>
        </Row>

        <Row>
          <Col
            sm="10"
            className="offset-sm-1 receiptColColor receiptRoundedCorners"
          >
            <Row>
              <Col className="receiptTextCenter receiptHeading1 receiptPrimaryColor receiptRoundedTopCorners">
                <span>Cloud Space Details</span>
              </Col>
            </Row>

            <Row>
              <Col className="receiptTextCenter">
                <hr className="receiptHeadingHR" />
              </Col>
            </Row>

            <Row>
              <Col
                sm="10"
                className="offset-sm-1 receiptColColor receiptTextCenter"
              >
                <p className="receiptPrimaryColor">
                  Good news! Your cloud space is ready for activation. To
                  activate your WOW Cloud Network cloud space, please go to the{" "}
                  <a href="https://wowcloudnetwork.com/activation.html">
                    {" "}
                    activation page
                  </a>{" "}
                  and fill in the required information.
                </p>
              </Col>
            </Row>

            <Row>
              <Col
                sm="10"
                className="offset-sm-1 receiptHeading2 receiptPrimaryColor receiptColColor"
              >
                <span>Transaction Details</span>
              </Col>
            </Row>

            <ReceiptLineItem
              lineField="Transaction ID"
              lineValue={transaction.ctransid}
            />
            <ReceiptLineItem
              lineField="Invoice #"
              lineValue={transaction.invoicenumber}
            />
            <ReceiptLineItem
              lineField="Total"
              lineValue={"$" + transaction.price}
            />
            <ReceiptLineItem
              lineField="Payment Type"
              lineValue="Visa Master Card"
            />
            <ReceiptLineItem
              lineField="Referral / Promo Code"
              lineValue={transaction.referenceid1}
            />
            <ReceiptLineItem
              lineField="Storage Amount"
              lineValue={transaction.price + "GB"}
            />
            <ReceiptLineItem lineField="Phone" lineValue={customer.phone} />
            <ReceiptLineItem
              lineField="Activation Code"
              lineValue={transaction.activationcode}
            />
            <ReceiptLineItem
              lineField="Activation Expiration"
              lineValue={formatDate(transaction.datetimeexpire)}
            />
            <ReceiptLineItem
              lineField="Cloud Space Expiration"
              lineValue={formatDate(
                new Date(transaction.datetimecreated).setDate(
                  new Date(transaction.datetimecreated).getDate() + 365
                )
              )}
            />
            <ReceiptLineItem
              lineField="Trading Program Description"
              lineValue="******WOW"
            />
            <ReceiptLineItem
              lineField="User Description"
              lineValue={transaction.shortdescription}
            />

            <Row>
              <Col
                sm="10"
                className="offset-sm-1 receiptColColor"
                style={{
                  minHeight: "50px",
                }}
              ></Col>
            </Row>

            <Row>
              <Col
                sm="10"
                className="offset-sm-1 receiptColColor receiptTextCenter receiptActivationMessage"
              >
                <p>
                  This activation code is only valid for 120 days and the
                  non-renewable cloudspace is only available for 1 year from the
                  date of purchase.
                </p>
              </Col>
            </Row>

            <Row>
              <Col sm="10" className="offset-sm-1 receiptColColor">
                <hr className="receiptHR" />
              </Col>
            </Row>

            <ReceiptLineItem
              classStyles="receiptRowMinHeight"
              lineField="Order Date"
              lineValue={formatDate(transaction.datetimecreated)}
            />

            <Row>
              <Col sm="10" className="offset-sm-1 receiptColColor">
                <hr className="receiptHR" />
              </Col>
            </Row>

            <Row>
              <Col
                sm="10"
                className="offset-sm-1 receiptTextCenter receiptColColor receiptRoundedBottomCorners"
              >
                <p style={{ color: "#000" }}>
                  Have Questions? Contact Customer Support at{" "}
                  <a href="mailto:contact@wowcloudnetwork.com">
                    contact@wowcloudnetwork.com
                  </a>
                </p>
              </Col>
            </Row>
          </Col>
        </Row>
      </CardBody>

      <CardFooter className="receiptFooter receiptTextCenter">
        <Row>
          <Col>
            <p style={{ color: "#000" }}>
              WOW Cloud Network © {new Date().getFullYear()}, All right
              reserved.
            </p>
          </Col>
        </Row>
      </CardFooter>
    </div>
  );
});

ReceiptCard.displayName = "ReceiptCard";

export default ReceiptCard;
